import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  ContentButtonNavigation,
} from "hec/components/ui/carousel";
import { navButtonTypes } from "hec/components/ui/carousel.utils";

import { cn } from "~/lib/utils";

import ImageTextCarouselItem from "./image-text-item";
import type { ImageTextCarouselGridProps } from "./types";

/**
 * ImageTextCarouselGrid — editorial image + text cards rendered as a 3-column
 * grid on desktop and a swipeable carousel on mobile. Extracted from the
 * `homepageCarouselGrid` right panel (SFCC-11246) so it can be placed as a
 * standalone Builder block on any page, not only inside the homepage composite.
 *
 * Fully content-authored: no product API, no SSR pre-fetch, no
 * CarouselProductsProvider coupling.
 */
const ImageTextCarouselGrid: React.FC<ImageTextCarouselGridProps> = ({
  iconsHeaderText = "Popular Categories",
  items = [],
  slidesVisible,
  slidePadding,
  ButtonNavigationColor,
  className,
  attributes,
  "data-testid": dataTestId,
}) => {
  const { className: attrClassName, ...restAttributes } =
    (attributes as React.HTMLAttributes<HTMLDivElement>) ?? {};
  return (
    <div
      data-testid={dataTestId}
      {...restAttributes}
      className={cn(
        "right-container min-[992px]:min-h-[440px] flex min-h-[auto] w-full flex-col items-center justify-center gap-2 rounded-[12px] bg-white p-[18px] shadow-[0_4px_20px_0_rgba(0,0,0,0.22)] lg:justify-start lg:gap-4 lg:p-[28px]",
        className,
        attrClassName,
      )}
    >
      <div className="icon-container-header mb-4">
        <h2 className="text-center text-[26px] font-medium leading-[1.2] text-emphasis lg:text-[36px]">
          {iconsHeaderText}
        </h2>
      </div>

      {/* Desktop Grid View */}
      <div className="min-[992px]:grid hidden grid-cols-3 gap-2 xl:gap-6">
        {items?.map((item, index) => (
          <ImageTextCarouselItem key={`image-text-item-${index}`} {...item} />
        ))}
      </div>

      {/* Mobile Carousel View */}
      <div className="min-[992px]:hidden w-full">
        <Carousel
          opts={{ loop: true, align: "start" }}
          className="flex h-full flex-col justify-center"
        >
          <div className="flex items-center gap-2">
            <CarouselPrevious
              className="static translate-x-0 translate-y-0"
              overlapContent={false}
              iconAttributes={{ className: "h-6 w-6" }}
              data-testid="carousel-Previous"
            />
            <CarouselContent className="-ml-2 flex-1">
              {items?.map((item, index) => (
                <CarouselItem
                  key={`image-text-carousel-item-${index}`}
                  slidesVisible={(slidesVisible?.onDesktop || "3.25") as "3.25"}
                  slidesVisibleMobile={"3"}
                  showPeek={false}
                  showPeekMobile={false}
                  slidePadding={slidePadding?.onDesktop}
                  slidePaddingMobile={slidePadding?.onMobile}
                  className="sm:basis-1/4"
                  data-testid="imageTextCarousel-CarouselItem"
                >
                  <ImageTextCarouselItem {...item} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselNext
              className="static translate-x-0 translate-y-0"
              overlapContent={false}
              iconAttributes={{ className: "h-6 w-6" }}
              data-testid="carousel-Next"
            />
          </div>
          <ContentButtonNavigation
            buttonType={navButtonTypes.FlatButton}
            buttonColor={ButtonNavigationColor}
            className="mt-2"
            data-testid="imageTextCarousel-ContentButtonNavigation"
          />
        </Carousel>
      </div>
    </div>
  );
};

export default ImageTextCarouselGrid;
